import {
  buildRecentQueriesList,
  buildSearchBox,
  RecentQueriesList,
  RecentQueriesState,
  Suggestion,
} from '@coveo/headless';
import classNames from 'classnames';
import HeroIcon from 'components/helpers/Icons/HeroIcon/HeroIcon';
import { useCoveoEngine } from 'lib/coveo/coveo';
import { useRouter } from 'next/router';
import { ChangeEvent, forwardRef, Ref, useEffect, useRef, useState } from 'react';
import { AzBlueAtomicSearchBoxProps } from '../atomic-wrappers/AzBlueAtomicSearchBox';
import { AzBlueHeadlessAutoSuggestDropdown } from './AzBlueHeadlessAutoSuggestDropdown';

interface SearchBoxProps {
  callBack?: () => void;
  fields: AzBlueAtomicSearchBoxProps['fields'];
}

// This component is specifically used on the GlobalHeaderSearchBar and MobileGlobalHeaderSearchBar to render the header Coveo-powered search bar.
export const AzBlueHeadlessSearchBox = forwardRef(
  ({ callBack, fields }: SearchBoxProps, ref: Ref<HTMLInputElement>) => {
    const coveoEngine = useCoveoEngine();
    const router = useRouter();
    const [searchValue, setSearchValue] = useState('');
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(false);

    const controllerRef = useRef(
      buildSearchBox(coveoEngine, {
        options: {
          highlightOptions: {
            notMatchDelimiters: {
              open: '<strong>',
              close: '</strong>',
            },
            correctionDelimiters: {
              open: '<i>',
              close: '</i>',
            },
          },
          numberOfSuggestions: 8,
        },
      })
    );
    const controller = controllerRef.current;

    // Coveo Recent Queries Controller
    const [recentController] = useState<RecentQueriesList>(() =>
      buildRecentQueriesList(coveoEngine, { options: { maxLength: 10 } })
    );
    const [recentState, setRecentState] = useState<RecentQueriesState>(recentController.state);

    useEffect(() => {
      const unsubscribe = recentController.subscribe(() => setRecentState(recentController.state));
      return unsubscribe;
    }, [recentController]);

    const redirectUrl =
      router?.asPath.split('#')[0] === fields?.searchPage?.url
        ? undefined
        : fields?.searchPage?.url;

    const handleSubmit = (value?: string) => {
      const finalValue = (value ?? searchValue).trim();
      if (!finalValue) return;

      if (redirectUrl) {
        window.location.assign(`${redirectUrl}#q=${finalValue}`);
      } else {
        controller.updateText(finalValue);
        controller.submit();

        //clear search input after submission when on search page
        controller.clear();
        setSearchValue(controller.state.value);
      }
      callBack && callBack(); //hides the search bar after submission or selection
      setIsSuggestionsVisible(false);
    };

    const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
      // e.target.value gets the current value of the input field while edited
      const value = e?.target?.value;
      setSearchValue(value);
      controller.updateText(value);
      controller.showSuggestions();

      setIsSuggestionsVisible(value.trim().length > 0);
    };

    const handleClear = () => {
      //if the search input is empty, call the callback function which closes the search bar
      if (controller.state.value === '') {
        callBack && callBack();
      }
      controller.clear();
      setSearchValue(controller.state.value);

      setIsSuggestionsVisible(false);
    };

    useEffect(() => {
      const unsubscribe = controller.subscribe(() => {
        setSuggestions(controller.state.suggestions);
      });
      return () => unsubscribe();
    }, [controller]);

    const isEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter';

    return (
      <div
        className={classNames(
          'bg-white',
          'border',
          'border-gray',
          'md-border-0',
          'flex',
          'items-center',
          'h-12',
          'md:h-8',
          'py-[1px]',
          'px-2',
          'overflow-visible', // ensure dropdown is visible
          'relative',
          'rounded-lg',
          'w-full'
        )}
      >
        <button
          title="click to search"
          disabled={!searchValue}
          onClick={() => handleSubmit()}
          className={classNames('text-teal', 'absolute')}
          aria-label={fields?.searchBoxPlaceholder?.value}
        >
          <HeroIcon size="xs" icon={'MagnifyingGlassIcon'} />
        </button>

        <input
          ref={ref}
          className={classNames(
            'flex-grow',
            'focus:outline-none',
            'pl-7',
            'rounded-lg',
            'text-gray-dark',
            'w-full'
          )}
          value={searchValue}
          onChange={(e) => handleInput(e)}
          onKeyDown={(e) => isEnterKey(e) && handleSubmit()}
          onFocus={handleInput}
          placeholder={fields?.searchBoxPlaceholder?.value}
          enterKeyHint="search"
          aria-label={fields?.searchBoxPlaceholder?.value}
        />
        {isSuggestionsVisible && (
          <AzBlueHeadlessAutoSuggestDropdown
            suggestions={suggestions}
            showClearRecentSearches={false}
            // set to true if you want to show the clear recent searches button
            recentSearches={recentState.queries}
            searchValue={searchValue}
            onSelect={(value) => handleSubmit(value)}
            onClearRecentSearches={() => recentController.clear()}
          />
        )}

        <button
          className={classNames('text-gray-medium')}
          onClick={handleClear}
          aria-label="Clear Search"
        >
          <HeroIcon size="xxs" icon={'XMarkIcon'} />
        </button>
      </div>
    );
  }
);
